import m from "mithril";
import BaseComponent from '../../BaseComponent';
import Messages from "./view/Messages";
import {ComponentContext} from "../../IComponent";
import MessageInput from "./view/MessageInput";
import ChatbotApi from "./model/ChatbotApi";
import {ChatbotRequestError} from "./model/ChatbotRequestError";
import ChatbotErrorRecovery from "./model/ChatbotErrorRecovery";
import TConversation from "./model/TConversation";
import ChatbotState from "./model/ChatbotState";
import ImageDetail from "./view/ImageDetail";

export default class Chatbot extends BaseComponent {

    public readonly api: ChatbotApi;
    public readonly state: ChatbotState;

    private readonly messages: Messages;
    private readonly messageInput: MessageInput;
    private readonly imageDetail: ImageDetail;
    private readonly errorRecovery: ChatbotErrorRecovery;
    private removed = false;
    private expectedMessageCount = 0;

    constructor(
        $component: JQuery<HTMLElement>,
        context?: ComponentContext,
    ) {
        super($component, context);

        this.messages = new Messages(this);
        this.messageInput = new MessageInput(this);
        this.imageDetail = new ImageDetail(this);

        this.state = new ChatbotState();
        this.state.avatarPath = this.$component.data('avatar');
        this.state.nick = this.$component.data('nick');
        this.state.newConversationUrl = this.$component.data('url-new-conversation');

        this.api = new ChatbotApi({
            conversation: this.$component.data('url-get-conversation'),
            send: this.$component.data('url-send'),
            stream: this.$component.data('url-stream'),
            download: this.$component.data('url-download'),
        });
        this.errorRecovery = new ChatbotErrorRecovery(
            this.api,
            this.state,
            conversation => this.applyConversation(conversation),
            () => m.redraw(),
        );
    }

    public async init() {
        const conversation = await this.api.getConversation();
        if (this.removed) {
            return;
        }
        this.applyConversation(conversation);

        m.mount(this.$component[0], {
            view: () => [
                m(this.messages),
                m(this.messageInput),
                m(this.imageDetail),
            ],
        });

        this.state.started = Date.now();

        const lastMessage = this.state.messages[this.state.messages.length - 1]
        if (!this.state.locked && !this.state.hasError && lastMessage?.role === 'user' && lastMessage.content) {
            this.state.animateFirstMessageText = true;
            this.state.input = lastMessage.content;
            this.state.messages.pop();
            await this.send();
            this.state.animateFirstMessageText = false;
        }

    }


    public async send(): Promise<void> {
        if (this.removed || this.state.locked || this.state.sending || this.state.recovering) {
            return;
        }

        this.state.sending = true;
        this.expectedMessageCount = this.state.messages.length + 2;
        try {
            this.state.messages.push({
                role: 'user',
                content: this.state.input,
                completed: true,
                created: Date.now(),
            });

            this.state.messages.push({
                role: 'assistant',
                content: '...',
                completed: false,
                created: Date.now(),
            });
            try {
                await this.api.sendMessage(this.state.input, message => {
                    this.state.messages[this.state.messages.length - 1].content = message.content;
                    m.redraw();
                });
            } catch (err) {
                if (this.removed) {
                    return;
                }
                console.error(err);
                if (err instanceof ChatbotRequestError && err.postRejected) {
                    this.state.messages.splice(-2);
                    return;
                }
                this.state.recovering = true;
            }

            this.state.input = '';
            m.redraw();
            await this.recoverConversation();
            m.redraw();
        } catch (err) {
            console.error(err);
        } finally {
            this.state.sending = false;
            m.redraw.sync();
            this.$component.find('textarea').focus();
        }
        m.redraw();
    }

    public recoverConversation(): Promise<void> {
        return this.errorRecovery.recover(this.expectedMessageCount);
    }

    private applyConversation(conversation: TConversation): void {
        this.state.messages = conversation.messages ?? [];
        this.state.locked = conversation.locked ?? false;
        this.state.hasError = conversation.hasError ?? false;
    }

    public beforeRemove(): void {
        this.removed = true;
        this.errorRecovery.destroy();
        this.api.closeStream();
        m.mount(this.$component[0], null);
    }
}
